import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useAccountInfo } from '../api/queryHooks/account';
import { readAlbumAccess } from '../api/albumAccess';
import { useLogin } from './useLogin';
import { useVerificationWall } from './useVerificationWall';

/**
 * The registration wall of a shop whose `album_access` is "registered": a
 * visitor or guest may open an album by code, but only a registered user sees
 * it. Opening an album mints a guest session; that session is kept (never
 * cleared here) so a second code entered on the login page attaches to the
 * same guest and Login's stashed guest token carries both albums over.
 */
export const useAlbumWall = () => {
  const navigate = useNavigate();
  const { data: accountInfo } = useAccountInfo();
  const { isLoggedIn, isGuest, isPending } = useLogin();
  const wall = useVerificationWall();

  const registered = readAlbumAccess(accountInfo?.setting?.album_access) === 'registered';
  const realUser = isLoggedIn && !isGuest;

  // The shop setting may land while the open request is in flight, so read its
  // latest value at completion; the visitor's state is judged as of the call
  // (the closure), because the open itself may mint a guest session.
  const registeredRef = useRef(registered);
  registeredRef.current = registered;

  // A signed-in customer who has yet to confirm their email is not "walled"
  // (they are registered) but, in a registered shop, may not see albums until
  // they do: they go to the code page, not to login. The decision lives in
  // useVerificationWall; this only applies it to the album wall.
  const verificationRequired = registered && wall.pending;
  const verificationRef = useRef(verificationRequired);
  verificationRef.current = verificationRequired;

  const afterAlbumOpened = useCallback(
    (albumId?: string, opts?: { replace?: boolean }) => {
      const replace = opts?.replace ?? false;
      const target = albumId ? `/albums/${albumId}` : '/albums';
      if (verificationRef.current) {
        navigate('/verify-email/code', { replace, state: { from: target } });
      } else if (registeredRef.current && !realUser) {
        navigate('/login', { replace, state: { from: target, registrationRequired: true } });
      } else {
        navigate(target, { replace });
      }
    },
    [navigate, realUser],
  );

  // `ready`: the session answer is known, so the closure of afterAlbumOpened
  // holds a real "logged in or not" rather than a still-pending unknown.
  return {
    walled: registered && !realUser,
    verificationRequired,
    ready: !isPending && wall.ready,
    afterAlbumOpened,
  };
};
